import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { toast } from "sonner"
import {
  Settings, ChevronRight, Camera, Dumbbell, Award, UsersRound, Trophy, Loader2,
} from "lucide-react"
import { useAuth } from "@/contexts/AuthContext"
import { useProfile } from "@/hooks/useProfile"
import { useCurrentBodyweight } from "@/hooks/useCurrentBodyweight"
import { useProgramBlock } from "@/hooks/useProgramBlock"
import { useTestingSchedule } from "@/hooks/useTestingSchedule"
import { useStrengthRadar } from "@/hooks/useStrengthRadar"
import {
  getAthleteProfile, uploadAvatar,
  type AthleteProfileRow, type WizardSubmissionRow,
} from "@/lib/supabase"
import { normalizePhoto, PhotoPipelineError } from "@/lib/photoPipeline"
import { computeBmi, getBmiCategory } from "@/lib/bmi"
import { formatFr } from "@/lib/utils"
import {
  RM_FIELD_BY_MOVEMENT, MOVEMENT_LABELS, STRENGTH_MOVEMENTS, pullUpTotalFromRatio,
  type StrengthMovement,
} from "@/lib/strengthStandards"
import { BottomNav } from "@/components/BottomNav"
import { MeasurementsSection } from "@/components/profile/MeasurementsSection"
import { StrengthRadarSection } from "@/components/profile/StrengthRadarSection"
import { ProgressPhotosSection } from "@/components/profile/ProgressPhotosSection"
import { SectionTitle, SectionMeta, Card, CardButton, TONE } from "@/components/profile/ui"

interface ProfilePageProps { email: string; firstName?: string; goalId?: string; level?: string }

/**
 * Écran 1 du Profil.
 *
 * Ordre des blocs (lot 1, complété par les deux sections des lots 2a/2b) :
 *   1. en-tête — avatar, prénom, ancienneté et âge. Pas de pastille de niveau : elle
 *      faisait doublon avec l'écran Mon programme, qui est désormais son seul endroit ;
 *   2. trois tuiles Poids / Taille / IMC ;
 *   3. carte Coach Dashboard — admin uniquement, invisible en vue client ;
 *   4. Mon bloc — carte entièrement cliquable vers l'écran Mon programme ;
 *   5. Mes mesures (lot 2a) ;
 *   6. Mon profil de force (lot 2b) ;
 *   7. Mes records ;
 *   8. Mon évolution.
 *
 * Les deux sections des lots 2a/2b sont placées entre « Mon bloc » et « Mes records »,
 * en zone de suivi. Le radar vient juste avant les records parce que ses cinq axes SONT
 * la liste des records : synthèse d'abord, détail ensuite. Les mesures viennent juste
 * avant, ce qui enchaîne corps → force → détail des records et garde le code couleur
 * groupé (violet des mesures, puis ambre des records).
 *
 * Les paramètres du programme et tout ce qui touche au compte ont quitté cet écran :
 * ils vivent respectivement dans /dashboard/profile/program et /dashboard/profile/settings.
 */
export const ProfilePage = ({ email, firstName }: ProfilePageProps) => {
  const navigate = useNavigate()
  const { isAdmin, user } = useAuth()
  const { profile, loading, updateProfile } = useProfile()
  // Poids actuel = dernière pesée (profile.weight n'est que le poids d'inscription).
  // Attendu avec le reste du chargement : pas d'ancien poids affiché une fraction de seconde.
  const { bodyweightKg, loading: bodyweightLoading } = useCurrentBodyweight(user?.id)
  const block = useProgramBlock()
  // Un seul calendrier de tests, partagé par « Mes mesures » et « Mon profil de force » :
  // les deux sections lisent la même échéance au lieu d'en recalculer chacune une.
  const schedule = useTestingSchedule(block)

  const [athleteProfile, setAthleteProfile] = useState<AthleteProfileRow | null>(null)
  const [loadingAthlete, setLoadingAthlete] = useState(true)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const avatarInput = useRef<HTMLInputElement | null>(null)

  const wizardRow = (profile?.raw ?? null) as WizardSubmissionRow | null
  const { data: strengthData, loading: strengthLoading } = useStrengthRadar(wizardRow)

  const displayName = firstName
    ? firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()
    : profile?.firstName ?? "Athlète"

  useEffect(() => {
    let mounted = true
    getAthleteProfile()
      .then((data) => { if (mounted) setAthleteProfile(data) })
      .finally(() => { if (mounted) setLoadingAthlete(false) })
    return () => { mounted = false }
  }, [])

  const handleAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file || uploadingAvatar) return
    setUploadingAvatar(true)
    try {
      const { blob } = await normalizePhoto(file)
      const url = await uploadAvatar(blob)
      const { error } = await updateProfile({ avatar_url: url })
      if (error) throw new Error(error)
      toast.success("Photo de profil mise à jour")
    } catch (err) {
      const msg = err instanceof PhotoPipelineError ? err.message
        : err instanceof Error ? err.message : "Envoi échoué"
      toast.error("Photo non enregistrée", { description: msg })
    } finally {
      setUploadingAvatar(false)
    }
  }

  if (loading || !profile || bodyweightLoading) {
    return (
      <div className="min-h-screen bg-[#060a13] flex items-center justify-center cursor-default">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-full border-2 border-cyan-500/20 border-t-cyan-400 animate-spin" />
          <p className="text-[13px] text-gray-500">Chargement du profil…</p>
        </div>
      </div>
    )
  }

  const { weightStart, height, initials, memberSince, age } = profile
  // Tuile Poids, écart avec le départ, IMC (libellé OMS et couleur) et masse maigre :
  // tous sur la dernière pesée et la taille du profil.
  const weight = bodyweightKg ?? profile.weight
  const weightDiff = weight - weightStart

  const bmi = computeBmi(weight, height)
  const bmiCategory = getBmiCategory(bmi)

  // Cible du bloc en cours par mouvement, affichée à côté du record (lot 2b).
  // Elle est en kg pour les mouvements à la barre ; pour la traction, la cible est stockée
  // en ratio et reconvertie ici en CHARGE TOTALE — c'est l'unité de `rm_pull_up`, donc
  // celle du record affiché juste à côté. Mélanger charge totale et lest sur la même ligne
  // rendrait les deux nombres incomparables.
  const targetByMovement = new Map<StrengthMovement, string>()
  for (const movement of STRENGTH_MOVEMENTS) {
    const entry = strengthData?.entries.find((e) => e.movement === movement)
    if (!entry?.targetValue || !strengthData) continue
    const displayed = movement === "traction_lestee"
      ? pullUpTotalFromRatio(entry.targetValue, strengthData.bodyweightKg)
      : entry.targetValue
    if (displayed == null) continue
    targetByMovement.set(movement, Math.round(displayed) + " kg")
  }

  const rmList = STRENGTH_MOVEMENTS.map((movement) => {
    const entry = strengthData?.entries.find((e) => e.movement === movement) ?? null
    const raw = athleteProfile?.[RM_FIELD_BY_MOVEMENT[movement] as keyof AthleteProfileRow]
    const value = typeof raw === "number" ? raw : null
    return {
      movement,
      label: MOVEMENT_LABELS[movement],
      value,
      target: targetByMovement.get(movement) ?? null,
      // Écart par rapport au test précédent du bloc — la seule référence datée dont on
      // dispose. Sans test initial enregistré, aucun écart n'est affiché plutôt qu'un +0.
      delta: initialValueInKg(entry, value),
    }
  })

  const recordsBeaten = rmList.filter((r) => r.delta != null && r.delta > 0).length

  return (
    <div className="min-h-screen bg-[#060a13] pb-28 cursor-default relative overflow-x-hidden">

      {/* Lumière ambiante cyan derrière l'avatar */}
      <div
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2 pointer-events-none"
        style={{
          top: -140, width: 460, height: 420,
          background: "radial-gradient(ellipse at center, rgba(6,182,212,0.16), rgba(6,182,212,0.04) 45%, transparent 70%)",
        }}
      />

      {/* EN-TÊTE : l'engrenage remplace l'icône de déconnexion, partie dans les Réglages */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="px-5 pt-5 pb-1 relative z-10 flex items-center">
        <span className="w-9" />
        <h1 className="flex-1 text-center text-[16px] font-semibold text-white tracking-tight">Profil</h1>
        <button
          onClick={() => navigate("/dashboard/profile/settings")}
          aria-label="Réglages"
          className="w-9 h-9 rounded-[11px] bg-gray-800/50 flex items-center justify-center hover:bg-gray-700/50 transition-colors cursor-pointer"
        >
          <Settings className="w-[17px] h-[17px] text-gray-400" />
        </button>
      </motion.div>

      {/* IDENTITÉ */}
      <motion.div
        initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
        className="px-5 mt-2 relative z-10 flex flex-col items-center"
      >
        <input ref={avatarInput} type="file" accept="image/*" className="hidden" onChange={handleAvatar} />
        <div className="relative mb-3">
          <div
            className="w-[100px] h-[100px] rounded-full flex items-center justify-center text-[30px] font-bold overflow-hidden"
            style={{
              background: "radial-gradient(circle at 50% 28%, rgba(6,182,212,0.24), rgba(6,182,212,0.03))",
              border: "2px solid #06b6d4",
              color: "#67e8f9",
              boxShadow: "0 0 34px -8px rgba(6,182,212,0.55)",
            }}
          >
            {profile.raw.avatar_url
              ? <img src={profile.raw.avatar_url} alt="" className="w-full h-full object-cover" />
              : initials}
          </div>
          <button
            onClick={() => avatarInput.current?.click()}
            disabled={uploadingAvatar}
            aria-label="Changer la photo de profil"
            className="absolute bottom-0 right-0 w-8 h-8 rounded-full flex items-center justify-center cursor-pointer disabled:opacity-60"
            style={{ background: "linear-gradient(135deg,#0369a1,#06b6d4)", border: "3px solid #060a13" }}
          >
            {uploadingAvatar
              ? <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
              : <Camera className="w-3.5 h-3.5 text-white" />}
          </button>
        </div>
        <h2 className="text-[26px] font-bold text-white tracking-tight">{displayName}</h2>
        <p className="text-[12px] text-gray-500 mt-2 tnum">
          Membre depuis {memberSince}{age != null ? ` · ${age} ans` : ""}
        </p>
      </motion.div>

      {/* TUILES */}
      <motion.div
        initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="px-4 mt-6 relative z-10 grid grid-cols-3 gap-2.5"
      >
        <Tile
          illustration="/illustrations/poids.svg"
          value={formatFr(weight, weight % 1 === 0 ? 0 : 1)}
          unit="kg"
          caption="POIDS"
          badge={weightDiff !== 0 ? {
            text: `${weightDiff > 0 ? "+" : "−"}${formatFr(Math.abs(weightDiff), 1)}`,
            color: weightDiff < 0 ? "#4ade80" : "#f97316",
          } : undefined}
          hint={`départ ${formatFr(weightStart, weightStart % 1 === 0 ? 0 : 1)} kg`}
        />
        <Tile illustration="/illustrations/taille.svg" value={String(height)} unit="cm" caption="TAILLE" />
        <Tile
          illustration="/illustrations/imc.svg"
          value={bmi != null ? formatFr(bmi, 1) : "—"}
          caption={bmiCategory ? `IMC · ${bmiCategory.label}` : "IMC"}
          captionColor={bmiCategory?.color}
        />
      </motion.div>

      {/* COACH DASHBOARD (admin) */}
      {isAdmin && (
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className="px-4 mt-3 relative z-10">
          <CardButton onClick={() => navigate("/admin/users")}>
            <div className="flex items-center gap-3 p-3.5">
              <span className="w-[42px] h-[42px] rounded-[13px] grid place-items-center shrink-0"
                style={{ background: TONE.cyan.bg, border: `1px solid ${TONE.cyan.border}`, color: TONE.cyan.fg }}>
                <UsersRound className="w-5 h-5" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[15px] font-semibold text-white">Coach Dashboard</span>
                <span className="block text-[12px] text-gray-500 mt-0.5">Gérer les utilisateurs</span>
              </span>
              <span className="text-[9.5px] font-bold px-1.5 py-1 rounded-md shrink-0"
                style={{ color: "#fbbf24", background: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.30)", letterSpacing: 1.1 }}>
                ADMIN
              </span>
            </div>
          </CardButton>
        </motion.div>
      )}

      {/* MON BLOC */}
      <motion.section initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }} className="px-4 mt-6 relative z-10">
        <SectionTitle label="Mon bloc" />
        <CardButton onClick={() => navigate("/dashboard/profile/program")}>
          <div className="p-4">
            <div className="flex items-start gap-3">
              <span className="w-[42px] h-[42px] rounded-[13px] grid place-items-center shrink-0"
                style={{ background: TONE.cyan.bg, border: `1px solid ${TONE.cyan.border}`, color: TONE.cyan.fg }}>
                <Dumbbell className="w-5 h-5" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[15px] font-semibold text-white truncate">{block.programName}</span>
                {/* Le mot « Progression » a disparu : la seule mesure d'avancement du bloc
                    est la semaine, et elle est écrite en toutes lettres ici. */}
                <span className="block text-[12px] text-gray-500 mt-0.5 tnum">
                  Semaine {block.weekCurrent} / {block.weeksTotal}
                </span>
              </span>
              <span className="text-right shrink-0">
                <span className="text-[19px] font-bold text-white tnum">{block.sessionsCompleted}</span>
                <span className="text-[12.5px] text-gray-500 tnum">/{block.sessionsTotal || "—"}</span>
                <span className="block text-[9.5px] text-gray-600 mt-0.5" style={{ letterSpacing: 1 }}>SÉANCES</span>
              </span>
            </div>

            {/* Une barre, un segment par semaine du bloc. */}
            <div className="flex gap-1 mt-4">
              {Array.from({ length: block.weeksTotal }, (_, i) => {
                const done = i + 1 < block.weekCurrent
                const current = i + 1 === block.weekCurrent
                return (
                  <span
                    key={i}
                    className="flex-1 h-[5px] rounded-full"
                    style={{
                      background: done
                        ? "#06b6d4"
                        : current
                          ? "linear-gradient(90deg, #06b6d4 50%, rgba(255,255,255,0.08) 50%)"
                          : "rgba(255,255,255,0.08)",
                      boxShadow: done || current ? "0 0 8px -2px rgba(6,182,212,0.7)" : undefined,
                    }}
                  />
                )
              })}
            </div>
            <div className="flex justify-between mt-2 text-[10px] text-gray-600 tnum" style={{ letterSpacing: 1 }}>
              <span>S1</span><span>S{block.weeksTotal}</span>
            </div>

            {block.isFinalWeek && (
              <div className="flex items-center gap-3 mt-4 p-3 rounded-[15px]"
                style={{ border: "1px solid rgba(6,182,212,0.28)", background: "linear-gradient(100deg, rgba(6,182,212,0.15), rgba(6,182,212,0.04))" }}>
                <span className="w-9 h-9 rounded-full grid place-items-center shrink-0"
                  style={{ border: "1.5px solid rgba(6,182,212,0.5)", color: "#67e8f9" }}>
                  <Trophy className="w-4 h-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[14px] font-semibold text-white">Dernière ligne droite</span>
                  <span className="block text-[11.5px] mt-0.5" style={{ color: "#8ecfdd" }}>
                    {block.nextTestSession
                      ? `Test de force à la séance ${block.nextTestSession}, puis nouveau bloc`
                      : "Test de force, puis passage au bloc suivant"}
                  </span>
                </span>
              </div>
            )}
          </div>
        </CardButton>
      </motion.section>

      {/* MES MESURES (lot 2a) */}
      <MeasurementsSection gender={profile.raw.gender} heightCm={height} currentWeightKg={weight} schedule={schedule} />

      {/* MON PROFIL DE FORCE (lot 2b) */}
      <StrengthRadarSection data={strengthData} loading={strengthLoading} schedule={schedule} />

      {/* MES RECORDS */}
      <motion.section initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }} className="px-4 mt-6 relative z-10">
        <SectionTitle label="Mes records" right={<SectionMeta>1RM</SectionMeta>} />
        <Card>
          {loadingAthlete ? (
            <p className="text-[12px] text-gray-500 text-center py-6">Chargement…</p>
          ) : (
            rmList.map((rm, i, arr) => (
              <button
                key={rm.movement}
                onClick={() => navigate(`/dashboard/profile/records/${rm.movement}`)}
                className="w-full flex items-center gap-3.5 px-4 py-3.5 cursor-pointer transition-all hover:bg-white/[0.02] active:scale-[0.995] text-left"
                style={{ borderBottom: i < arr.length - 1 ? "1px solid rgba(30,41,59,0.35)" : "none" }}
              >
                <span className="w-[34px] h-[34px] rounded-[11px] grid place-items-center shrink-0"
                  style={{ background: TONE.amber.bg, border: `1px solid ${TONE.amber.border}`, color: TONE.amber.fg }}>
                  <Award className="w-4 h-4" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[14px] font-medium text-gray-200 truncate">{rm.label}</span>
                  {/* Cible du bloc en cours — libellé « Cible » uniquement, jamais un nom de niveau. */}
                  {rm.target && <span className="block text-[11.5px] text-gray-500 mt-0.5 tnum">Cible du bloc · {rm.target}</span>}
                </span>
                {rm.delta != null && rm.delta !== 0 && (
                  <span className="text-[10.5px] font-semibold px-1.5 py-1 rounded-md shrink-0 tnum"
                    style={{
                      color: rm.delta > 0 ? "#4ade80" : "#f87171",
                      background: rm.delta > 0 ? "rgba(74,222,128,0.12)" : "rgba(248,113,113,0.12)",
                    }}>
                    {rm.delta > 0 ? "+" : "−"}{formatFr(Math.abs(rm.delta), Math.abs(rm.delta) % 1 === 0 ? 0 : 1)}
                  </span>
                )}
                <span className="text-[15px] font-bold text-white shrink-0 tnum">
                  {rm.value != null ? `${formatFr(rm.value, rm.value % 1 === 0 ? 0 : 1)} kg`
                    : <span className="text-gray-600 font-normal text-[12px]">Non testé</span>}
                </span>
                <ChevronRight className="w-4 h-4 text-gray-700 shrink-0" />
              </button>
            ))
          )}
        </Card>
        <p className="text-[11.5px] text-gray-500 leading-relaxed mt-2.5 mx-1">
          Écart affiché par rapport à ton test précédent.
        </p>
      </motion.section>

      {/* MON ÉVOLUTION */}
      <ProgressPhotosSection
        weekCurrent={block.weekCurrent}
        weeksTotal={block.weeksTotal}
        recordsBeaten={recordsBeaten}
      />

      <p className="text-center text-[11px] text-gray-700 mt-8 px-5 tnum">{email || profile.email}</p>

      <BottomNav />
    </div>
  )
}

/**
 * Écart en KG entre le record courant et le test initial du bloc.
 *
 * Piège d'unité : sur la traction lestée, `currentValue` et `initialValue` sont des
 * RATIOS de charge totale, pas des kilos — les soustraire donnerait « +0,2 kg » là où le
 * client a gagné 15 kg. On repart donc de la valeur brute d'athlete_profiles (déjà en kg
 * de charge totale) et on reconvertit le ratio initial avec le poids de corps de l'époque,
 * celui avec lequel il a été calculé.
 */
function initialValueInKg(
  entry: { movement: StrengthMovement; currentValue: number | null; initialValue: number | null; initialBodyweightKg: number | null } | null,
  currentRawKg: number | null
): number | null {
  if (!entry || entry.initialValue == null) return null
  if (entry.movement === "traction_lestee") {
    if (currentRawKg == null || entry.initialBodyweightKg == null) return null
    const initialKg = pullUpTotalFromRatio(entry.initialValue, entry.initialBodyweightKg)
    return initialKg == null ? null : currentRawKg - initialKg
  }
  if (entry.currentValue == null) return null
  return entry.currentValue - entry.initialValue
}

// ── Tuile ───────────────────────────────────────────────────────────────────
function Tile({
  illustration, value, unit, caption, captionColor, badge, hint,
}: {
  illustration: string
  value: string
  unit?: string
  caption: string
  captionColor?: string
  badge?: { text: string; color: string }
  hint?: string
}) {
  return (
    <Card className="p-3 pt-4 text-center">
      {badge && (
        <span className="absolute top-2.5 right-2.5 text-[10.5px] font-bold px-1.5 py-0.5 rounded-md tnum"
          style={{ color: badge.color, background: `${badge.color}22` }}>
          {badge.text}
        </span>
      )}
      <img src={illustration} alt="" aria-hidden className="w-[52px] h-[52px] mx-auto mb-3" />
      <div className="text-[24px] font-bold text-white leading-none tracking-tight tnum">
        {value}{unit && <span className="text-[12px] font-medium text-gray-500 ml-0.5">{unit}</span>}
      </div>
      <div className="mt-2 text-[9.5px] leading-tight" style={{ color: captionColor ?? "#6b7280", letterSpacing: 0.7 }}>
        {caption.toUpperCase()}
      </div>
      {hint && <div className="mt-1 text-[9.5px] text-gray-700 tnum">{hint}</div>}
    </Card>
  )
}
